import { useState } from 'react';
import { Settings } from '../../../mocks/step6Data';

interface Step6PlaniProps {
  settings: Settings;
}

function calcAllSteps(settings: Settings, teamSize: number, step1Count: number) {
  const l1 = teamSize;
  const l2 = teamSize ** 2;
  const l3 = teamSize ** 3;
  const l4 = teamSize ** 4;
  const l5 = teamSize ** 5;
  const l6 = teamSize ** 6;

  const step1Income = step1Count * settings.kayitPrimi + l1 * settings.seviye1Kazanc;
  const step2Income = step1Income + l2 * settings.seviye2Kazanc;
  const step3Income = step2Income + l3 * settings.seviye3Kazanc;
  const step4Income = step3Income + l4 * settings.seviye4Kazanc;
  const step5Income = step4Income + l5 * settings.seviye5Kazanc;
  const step6Income = step5Income + l6 * settings.seviye6Kazanc;

  return { step1Income, step2Income, step3Income, step4Income, step5Income, step6Income, l1, l2, l3, l4, l5, l6 };
}

const STEP_COLORS = [
  { color: 'text-amber-400', bg: 'rgba(245,166,35,0.08)', bar: '#F5A623', border: 'rgba(245,166,35,0.18)', ring: 'rgba(245,166,35,0.25)' },
  { color: 'text-sky-400', bg: 'rgba(56,189,248,0.08)', bar: '#38BDF8', border: 'rgba(56,189,248,0.18)', ring: 'rgba(56,189,248,0.25)' },
  { color: 'text-purple-400', bg: 'rgba(168,85,247,0.08)', bar: '#A78BFA', border: 'rgba(168,85,247,0.18)', ring: 'rgba(168,85,247,0.25)' },
  { color: 'text-rose-400', bg: 'rgba(251,113,133,0.08)', bar: '#FB7185', border: 'rgba(251,113,133,0.18)', ring: 'rgba(251,113,133,0.25)' },
  { color: 'text-emerald-400', bg: 'rgba(52,211,153,0.08)', bar: '#34D399', border: 'rgba(52,211,153,0.18)', ring: 'rgba(52,211,153,0.25)' },
  { color: 'text-orange-400', bg: 'rgba(251,146,60,0.08)', bar: '#FB923C', border: 'rgba(251,146,60,0.18)', ring: 'rgba(251,146,60,0.25)' },
];

export default function Step6Plani({ settings }: Step6PlaniProps) {
  const [scenario, setScenario] = useState<4 | 2>(4);
  const income = calcAllSteps(settings, scenario, scenario);

  const maxIncome = income.step6Income || 1;

  const steps = [
    {
      step: 1, label: '1. Adım', income: income.step1Income,
      members: income.l1, desc: 'Kayıt primleri + 1. seviye ekip kazancı',
      icon: 'ri-user-add-line',
    },
    {
      step: 2, label: '2. Adım', income: income.step2Income,
      members: income.l1 + income.l2, desc: '+ 2. jenerasyon ekip kazancı',
      icon: 'ri-team-line',
    },
    {
      step: 3, label: '3. Adım', income: income.step3Income,
      members: income.l1 + income.l2 + income.l3, desc: '+ 3. jenerasyon ekip kazancı',
      icon: 'ri-bar-chart-fill',
    },
    {
      step: 4, label: '4. Adım', income: income.step4Income,
      members: income.l1 + income.l2 + income.l3 + income.l4, desc: '+ 4. jenerasyon ekip kazancı',
      icon: 'ri-bar-chart-2-fill',
    },
    {
      step: 5, label: '5. Adım', income: income.step5Income,
      members: income.l1 + income.l2 + income.l3 + income.l4 + income.l5, desc: '+ 5. jenerasyon ekip kazancı',
      icon: 'ri-line-chart-line',
    },
    {
      step: 6, label: '6. Adım', income: income.step6Income,
      members: income.l1 + income.l2 + income.l3 + income.l4 + income.l5 + income.l6, desc: '+ 6. jenerasyon ekip kazancı — TAM NETWORK',
      icon: 'ri-trophy-fill',
    },
  ];

  const incomes = [income.step1Income, income.step2Income, income.step3Income, income.step4Income, income.step5Income, income.step6Income];
  const breakEvenStep = incomes.findIndex(v => v >= settings.aktiflikBedeli) + 1 || 6;
  const profitStep = incomes.findIndex(v => v > settings.aktiflikBedeli) + 1 || 6;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">STEP6 Planım</h1>
        <p className="text-sm text-gray-500 mt-1">Kazanç simülasyonu ve adım analizin</p>
      </div>

      {/* Info Banner */}
      <div className="rounded-2xl p-4 flex items-center gap-3" style={{ background: 'rgba(245,166,35,0.06)', border: '1px solid rgba(245,166,35,0.15)' }}>
        <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(245,166,35,0.12)' }}>
          <i className="ri-information-line text-amber-400 text-base" />
        </div>
        <p className="text-xs text-amber-300/80">
          Aşağıdaki rakamlar <strong className="text-amber-400">Ayarlar / Kazanç Planı</strong> bölümünden güncellenir. Orada yaptığın her değişiklik bu sayfayı anında etkiler.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Summary */}
        <div className="rounded-2xl p-6" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(245,166,35,0.12)' }}>
              <i className="ri-settings-4-line text-amber-400 text-base" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white">Plan Parametreleri</h2>
              <p className="text-xs text-gray-500">Kayıtlı kazanç değerleri</p>
            </div>
          </div>

          <div className="space-y-2">
            {[
              { label: 'Aktiflik (aylık)', key: 'aktiflikBedeli' as const, icon: 'ri-shopping-bag-3-line', color: 'text-red-400' },
              { label: 'Kayıt Primi', key: 'kayitPrimi' as const, icon: 'ri-user-add-line', color: 'text-green-400' },
              { label: '1. Seviye', key: 'seviye1Kazanc' as const, icon: 'ri-arrow-right-s-line', color: 'text-amber-400' },
              { label: '2. Seviye', key: 'seviye2Kazanc' as const, icon: 'ri-arrow-right-s-line', color: 'text-sky-400' },
              { label: '3. Seviye', key: 'seviye3Kazanc' as const, icon: 'ri-arrow-right-s-line', color: 'text-purple-400' },
              { label: '4. Seviye', key: 'seviye4Kazanc' as const, icon: 'ri-arrow-right-s-line', color: 'text-rose-400' },
              { label: '5. Seviye', key: 'seviye5Kazanc' as const, icon: 'ri-arrow-right-s-line', color: 'text-emerald-400' },
              { label: '6. Seviye', key: 'seviye6Kazanc' as const, icon: 'ri-arrow-right-s-line', color: 'text-orange-400' },
            ].map(field => (
              <div key={field.key} className="flex items-center gap-2 p-2.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${field.icon} text-xs ${field.color}`} />
                </div>
                <span className="flex-1 text-xs text-gray-400">{field.label}</span>
                <span className="text-xs font-bold text-white">₺{settings[field.key].toLocaleString('tr-TR')}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Scenario Simulator */}
        <div className="lg:col-span-2 rounded-2xl p-6" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,180,255,0.12)' }}>
                <i className="ri-test-tube-line text-sky-400 text-base" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-white">6 Adım Simülatörü</h2>
                <p className="text-xs text-gray-500">Tüm adımlardaki kümülatif gelirin</p>
              </div>
            </div>
            <div className="flex gap-2">
              {([4, 2] as const).map(sc => (
                <button key={sc} onClick={() => setScenario(sc)}
                  className="px-4 py-1.5 rounded-xl text-xs font-semibold cursor-pointer transition-all whitespace-nowrap"
                  style={scenario === sc
                    ? { background: 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)', color: 'white' }
                    : { background: 'rgba(255,255,255,0.05)', color: '#6B7280', border: '1px solid rgba(255,255,255,0.06)' }}>
                  {sc} Kişi Senaryosu
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {steps.map((s, i) => {
              const c = STEP_COLORS[i];
              const barPct = Math.min(100, (s.income / maxIncome) * 100);
              const isProfitable = s.income > settings.aktiflikBedeli;
              return (
                <div key={s.step} className="rounded-xl p-3" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
                  <div className="flex items-center gap-3">
                    {/* Step badge */}
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: c.ring }}>
                      <i className={`${s.icon} text-sm ${c.color}`} />
                    </div>

                    {/* Label + desc */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-semibold text-white">{s.label}</span>
                        <span className="text-xs text-gray-600">{s.members.toLocaleString('tr-TR')} kişilik ağ</span>
                        {isProfitable && (
                          <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,0.15)', color: '#4ADE80' }}>
                            ✓ Kârlı
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                          <div className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${barPct}%`, background: c.bar }} />
                        </div>
                        <span className="text-xs text-gray-500 flex-shrink-0">{Math.round(barPct)}%</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">{s.desc}</p>
                    </div>

                    {/* Income */}
                    <div className="text-right flex-shrink-0">
                      <div className={`text-base font-black ${c.color}`}>₺{s.income.toLocaleString('tr-TR')}</div>
                      {s.step > 1 && (
                        <div className="text-xs text-gray-600">
                          +₺{(s.income - steps[i - 1].income).toLocaleString('tr-TR')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Growth summary row */}
          <div className="mt-4 p-3 rounded-xl flex items-center justify-between gap-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <div className="text-center flex-1">
              <div className="text-xs text-gray-500 mb-1">1. Adım Geliri</div>
              <div className="text-sm font-bold text-amber-400">₺{income.step1Income.toLocaleString('tr-TR')}</div>
            </div>
            <i className="ri-arrow-right-line text-gray-600 flex-shrink-0" />
            <div className="text-center flex-1">
              <div className="text-xs text-gray-500 mb-1">3. Adım Geliri</div>
              <div className="text-sm font-bold text-purple-400">₺{income.step3Income.toLocaleString('tr-TR')}</div>
            </div>
            <i className="ri-arrow-right-line text-gray-600 flex-shrink-0" />
            <div className="text-center flex-1">
              <div className="text-xs text-gray-500 mb-1">6. Adım Geliri</div>
              <div className="text-sm font-bold text-orange-400">₺{income.step6Income.toLocaleString('tr-TR')}</div>
            </div>
            {income.step1Income > 0 && (
              <>
                <i className="ri-arrow-right-line text-gray-600 flex-shrink-0" />
                <div className="text-center flex-1">
                  <div className="text-xs text-gray-500 mb-1">Büyüme Çarpanı</div>
                  <div className="text-sm font-bold text-emerald-400">×{Math.round(income.step6Income / income.step1Income).toLocaleString('tr-TR')}</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Critical Insights */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-2xl p-5 flex items-center gap-4" style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(34,197,94,0.15)' }}>
            <i className="ri-shield-check-line text-green-400 text-xl" />
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Aktiflik Karşılanıyor</p>
            <p className="text-sm font-bold text-white">{breakEvenStep}. Adımda</p>
            <p className="text-xs text-gray-500 mt-0.5">Aylık ₺{settings.aktiflikBedeli.toLocaleString('tr-TR')} aktifliğin karşılandığı adım</p>
          </div>
        </div>

        <div className="rounded-2xl p-5 flex items-center gap-4" style={{ background: 'rgba(245,166,35,0.06)', border: '1px solid rgba(245,166,35,0.15)' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(245,166,35,0.15)' }}>
            <i className="ri-money-dollar-circle-line text-amber-400 text-xl" />
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Net Kâr Başlıyor</p>
            <p className="text-sm font-bold text-white">{profitStep}. Adımda</p>
            <p className="text-xs text-gray-500 mt-0.5">Bu adımdan itibaren her ay kâr elde edersin</p>
          </div>
        </div>
      </div>
    </div>
  );
}
